import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks";
import { OnboardingAssetRequest } from "types/AssetRequest";
import AssetRequestStatusChip from "./AssetRequestStatusChip";
import { canSeeAssetRequestsFor } from "./asset-requests-access";
import { formatRequestDay } from "./asset-requests-helpers";

type Props = {
  companyId: string;
  /** The request the hiring flow created for this onboarding; none until step 1 is completed. */
  assetRequest: OnboardingAssetRequest | null | undefined;
  /** Step 1 is behind us (or the onboarding ended): a missing request will not appear any more. */
  stepOneDone: boolean;
};

function RequestState({ assetRequest, stepOneDone }: Pick<Props, "assetRequest" | "stepOneDone">) {
  const { t } = useTranslation();
  if (!assetRequest) {
    return (
      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        {t(stepOneDone ? "asset_requests_onboarding_absent" : "asset_requests_onboarding_none")}
      </span>
    );
  }
  if (assetRequest.status === "done") {
    const date = formatRequestDay(assetRequest.resolvedAt);
    return <AssetRequestStatusChip status="done" label={t("asset_requests_onboarding_done", { date })} />;
  }
  if (assetRequest.status === "rejected") {
    const reason = assetRequest.resolutionNote;
    return <AssetRequestStatusChip status="rejected" label={t("asset_requests_onboarding_rejected", { reason })} />;
  }
  return <AssetRequestStatusChip status="pending" />;
}

/** «Turto prašymas: Laukia» line on the onboarding card, with a link to the queue for those who issue equipment. */
export default function OnboardingAssetRequestLine({ companyId, assetRequest, stepOneDone }: Props) {
  const { t } = useTranslation();
  const role = useAppSelector((state) => state.authReducer.role);

  return (
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
      <p className="text-sm text-gray-500">{t("asset_requests_onboarding_label")}:</p>
      <RequestState assetRequest={assetRequest} stepOneDone={stepOneDone} />
      {canSeeAssetRequestsFor(role, companyId) && (
        <Link to={`/app/${companyId}/assets/requests`} className="text-sm text-blue-700 hover:underline">
          {t("asset_requests_open_queue")}
        </Link>
      )}
    </div>
  );
}
